/**
 * Mini Calendar Component
 * Compact calendar with event indicators and month navigation
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '@/lib/api';
import { getEventColor } from '@/lib/eventColors';
import { useEventColors } from '@/context/EventColorContext';
import { queryKeys } from '@/lib/queryKeys';

interface CalendarEvent {
  id: string;
  title: string;
  start_date: string;
  end_date: string;
  type: string;
  color?: string;
}

interface MiniCalendarProps {
  onDateSelect?: (date: Date) => void;
  currentDate?: Date; // Optional controlled date
  onMonthChange?: (date: Date) => void; // Callback when month changes
  selectedDate?: Date | null; // External selected date to highlight
}

export function MiniCalendar({ onDateSelect, currentDate: controlledDate, onMonthChange, selectedDate: externalSelectedDate }: MiniCalendarProps) {
  const navigate = useNavigate();
  const { eventTypeColors } = useEventColors();
  const [internalDate, setInternalDate] = useState(new Date());
  const [internalSelectedDate, setInternalSelectedDate] = useState<Date | null>(null);

  // Use external selected date if provided, otherwise use internal state
  const selectedDate = externalSelectedDate !== undefined ? externalSelectedDate : internalSelectedDate;

  // Use controlled date if provided, otherwise use internal state
  const currentDate = controlledDate || internalDate;

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const { data: rawEvents, isLoading } = useQuery({
    queryKey: queryKeys.allEventsAndTours,
    queryFn: async () => {
      const result = await apiGet<CalendarEvent[]>('/events');
      if (!result.success) throw new Error(result.error || 'Failed to load events');
      return Array.isArray(result.data) ? result.data : [];
    },
    staleTime: 0,
  });
  // Safety: ensure events is always an array (cache corruption guard)
  const events: CalendarEvent[] = Array.isArray(rawEvents) ? rawEvents : [];

  // Get events for a specific date
  const getEventsForDate = (date: Date): CalendarEvent[] => {
    // Format date as YYYY-MM-DD in local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    return events.filter(event => {
      // Extract date portion from ISO string without timezone conversion
      const startDate = event.start_date.split('T')[0];
      const endDate = event.end_date.split('T')[0];
      return dateStr >= startDate && dateStr <= endDate;
    });
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const days = generateCalendarDays();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const handlePrevMonth = () => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    if (controlledDate && onMonthChange) {
      // If controlled, notify parent
      onMonthChange(newDate);
    } else {
      // If uncontrolled, update internal state
      setInternalDate(newDate);
      // Also navigate to calendar if on calendar page
      const path = window.location.pathname;
      if (path.includes('/calendar')) {
        navigate(`/calendar?date=${newDate.toISOString().split('T')[0]}`);
      }
    }
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    if (controlledDate && onMonthChange) {
      // If controlled, notify parent
      onMonthChange(newDate);
    } else {
      // If uncontrolled, update internal state
      setInternalDate(newDate);
      // Also navigate to calendar if on calendar page
      const path = window.location.pathname;
      if (path.includes('/calendar')) {
        navigate(`/calendar?date=${newDate.toISOString().split('T')[0]}`);
      }
    }
  };

  const handleDateClick = (date: Date) => {
    setInternalSelectedDate(date);
    if (onDateSelect) {
      onDateSelect(date);
    } else {
      // Navigate to calendar view with selected date
      navigate(`/calendar?date=${date.toISOString().split('T')[0]}`);
    }
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Calendar</h3>
        <div className="flex items-center space-x-1">
          <button
            onClick={handlePrevMonth}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            title="Previous month"
          >
            <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          </button>
          <button
            onClick={handleNextMonth}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            title="Next month"
          >
            <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>
      
      <div className="text-xs text-center text-gray-500 dark:text-gray-400 mb-2">
        {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1 text-center text-xs">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
          <div key={i} className="text-gray-500 dark:text-gray-400 font-medium py-1">{day}</div>
        ))}
        {days.map((date, index) => {
          const isToday = date.getTime() === today.getTime();
          const isCurrentMonth = date.getMonth() === currentDate.getMonth();
          const isSelected = selectedDate && date.getTime() === selectedDate.getTime();
          const dayEvents = getEventsForDate(date);
          const hasEvent = dayEvents.length > 0;

          return (
            <button
              key={index}
              onClick={() => handleDateClick(date)}
              className={`
                relative py-1 rounded transition-all duration-200
                ${isToday ? 'bg-brand-600 text-white hover:bg-brand-700 dark:bg-brand-500 dark:hover:bg-brand-600 font-semibold' : ''}
                ${isSelected && !isToday ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-900 dark:text-brand-100 font-semibold ring-2 ring-brand-400 dark:ring-brand-500 ring-inset' : ''}
                ${!isToday && !isSelected ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : ''}
                ${!isCurrentMonth ? 'text-gray-400 dark:text-gray-600' : 'text-gray-700 dark:text-gray-200'}
              `}
              title={isSelected && !isToday ? 'Selected date' : ''}
            >
              {date.getDate()}
              {hasEvent && !isToday && (
                <div className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2 flex space-x-0.5">
                  {dayEvents.slice(0, 3).map((event, i) => {
                    const colorConfig = getEventColor(event.type, event.color, eventTypeColors);
                    return (
                      <div
                        key={i}
                        className={`w-1 h-1 rounded-full ${colorConfig.dot || ''}`}
                        style={colorConfig.style?.dot ? { backgroundColor: colorConfig.style.dot } : undefined}
                        title={event.title}
                      ></div>
                    );
                  })}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

